"use client";

import { useState } from "react";
import type { FestivalQuizData } from "./quiz-data";
import { trackEvent } from "@/lib/analytics";

type Props = {
  festival: FestivalQuizData;
  matchPercent: number;
};

const VARIANTS = [
  (name: string, pct: number) =>
    `I got ${pct}% ${name}. What\u2019s your festival?`,
  (name: string, pct: number) =>
    `Apparently I should be working at ${name}. Reckon you can beat ${pct}%?`,
  (name: string, pct: number) => `${name} with a ${pct}% match. Your turn.`,
];

const QUIZ_URL = "https://peakwave.co/festivals/quiz";

export function ShareRow({ festival, matchPercent }: Props) {
  const [copied, setCopied] = useState<"link" | "instagram" | null>(null);

  const variantIndex = festival.slug.charCodeAt(0) % VARIANTS.length;
  const shareText = VARIANTS[variantIndex](festival.name, matchPercent);
  const shareUrl = `${QUIZ_URL}?r=${encodeURIComponent(festival.slug)}&pct=${matchPercent}`;

  function track(method: string) {
    trackEvent("festival_quiz_shared", { method, festival: festival.slug });
  }

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied("link");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Silently ignore clipboard errors in unsupported environments
    }
    track("copy_link");
  }

  function shareWhatsApp() {
    const url = `https://wa.me/?text=${encodeURIComponent(`${shareText} ${shareUrl}`)}`;
    window.open(url, "_blank", "noopener,noreferrer");
    track("whatsapp");
  }

  async function copyInstagram() {
    try {
      await navigator.clipboard.writeText(`${shareText}\n\n${shareUrl}`);
      setCopied("instagram");
      setTimeout(() => setCopied(null), 2000);
    } catch {
      // Silently ignore clipboard errors in unsupported environments
    }
    track("copy_instagram");
  }

  return (
    <div className="flex flex-wrap items-center justify-center gap-3 mt-8">
      <button
        onClick={copyLink}
        className="bg-sand/60 hover:bg-sand rounded-sm px-4 py-2.5 text-[13px] font-medium text-navy transition-colors"
      >
        {copied === "link" ? "Copied!" : "Copy link"}
      </button>
      <button
        onClick={shareWhatsApp}
        className="bg-sand/60 hover:bg-sand rounded-sm px-4 py-2.5 text-[13px] font-medium text-navy transition-colors"
      >
        Share on WhatsApp
      </button>
      <button
        onClick={copyInstagram}
        className="bg-sand/60 hover:bg-sand rounded-sm px-4 py-2.5 text-[13px] font-medium text-navy transition-colors"
      >
        {copied === "instagram" ? "Copied!" : "Copy for Instagram"}
      </button>
    </div>
  );
}
